/*
 * r16n (2026-10-21), design D:/NecroWork/r15k10-cf-evidence/DESIGN.md 2: story shares to Close Friends. The slot setting pair
 * #1109 "Probability Of Sharing To Close Friends (Min)" / #1110 "(Max)" - V1 defines it (slot-settings2.html, the 1101-1108 card;
 * GET /uiapi/settings/metadata gives it `min "0"`, `max "100"`), the server stores it (StoryCloseFriendsSetting), the worker rolls it
 * per 1101 share (15k.10: p between Min and Max, r < p -> the whole Close Friends list, else Your story). 50 / 50 is the default
 * for every slot (the owner, 2026-10-22; the server reads a NULL as 50); 0 / 0 = every share to Your story, as before; 100 / 100 =
 * every share to Close Friends.
 *
 * This file is the V3 side's live check of the pair, on the lib/blankSourceWarning.js pattern: a pure function, never a block. The
 * server's clamp is the real validation (filterDtoSettings + applySave: each value to 0..100, Max raised to Min when it is lower) and
 * the V3 save shows what it changed as an auto-adjustment; this hint says the same thing while the numbers are typed, so nobody
 * saves 20 / 10 and reads 20 / 20 back without knowing why. Nothing here defines the setting: the ids are V1's, the bounds are the
 * server's clamp, the field names come from FIELD_METADATA.
 */

export const STORY_CF_TAG = '[STORY-CF]';

export const STORY_CF_IDS = Object.freeze({ min: 1109, max: 1110 });

/** The server's clamp bounds (StoryCloseFriendsSetting), the same as V1's input `min="0" max="100"`. */
export const STORY_CF_LO = 0;
export const STORY_CF_HI = 100;
/** The server's default and what it reads a missing (NULL) value as: StoryCloseFriendsSetting.DEFAULT / NULL_MEANS = 50 (2026-10-22). */
export const STORY_CF_DEFAULT = 50;

const toNum = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (s === '' || s.toLowerCase() === 'null') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const clamp = (n) => Math.max(STORY_CF_LO, Math.min(STORY_CF_HI, n));

/**
 * What the server stores for a Min / Max the page holds (DESIGN 2.2): a missing value counts as the default 50 (the server's
 * NULL_MEANS), each one is clamped to 0..100, and a Max below the Min is raised to the Min. Returns { min, max } (whole numbers
 * when the input is).
 */
export function storyCfStored(min, max) {
  const a = toNum(min);
  const b = toNum(max);
  const lo = clamp(a === null ? STORY_CF_DEFAULT : a);
  let hi = clamp(b === null ? STORY_CF_DEFAULT : b);
  if (hi < lo) hi = lo;
  return { min: lo, max: hi };
}

/**
 * null when the pair is fine (whole numbers, 0 <= Min <= Max <= 100), else the hint:
 *   { title, severity: 'warn' | 'error', items: [{ code, text }], stored: { min, max }, logLine }
 * codes: MIN_RANGE / MAX_RANGE (outside 0-100: the server clamps it), ORDER (Max below Min: the server stores Max = Min),
 * NOT_WHOLE (a fraction: the server reads whole numbers only - error-toned). Pure.
 */
export function storyCfCheck(min, max) {
  const a = toNum(min);
  const b = toNum(max);
  const items = [];
  const whole = (n) => n === null || Number.isInteger(n);
  if (!whole(a) || !whole(b)) {
    const bad = [!whole(a) ? `#${STORY_CF_IDS.min} Min ${a}` : null, !whole(b) ? `#${STORY_CF_IDS.max} Max ${b}` : null].filter(Boolean);
    items.push({
      code: 'NOT_WHOLE',
      text: `${bad.join(' and ')} ${bad.length === 1 ? 'is not a whole number' : 'are not whole numbers'}: use a whole number from `
        + `${STORY_CF_LO} to ${STORY_CF_HI} (a percentage). The server reads whole numbers only.`,
    });
  }
  const stored = storyCfStored(a, b);
  if (a !== null && (a < STORY_CF_LO || a > STORY_CF_HI)) {
    items.push({ code: 'MIN_RANGE', text: `#${STORY_CF_IDS.min} Min ${a} is outside ${STORY_CF_LO}-${STORY_CF_HI}: the server stores ${clamp(a)}.` });
  }
  if (b !== null && (b < STORY_CF_LO || b > STORY_CF_HI)) {
    items.push({ code: 'MAX_RANGE', text: `#${STORY_CF_IDS.max} Max ${b} is outside ${STORY_CF_LO}-${STORY_CF_HI}: the server stores ${clamp(b)}.` });
  }
  if (a !== null && b !== null && clamp(b) < clamp(a)) {
    items.push({
      code: 'ORDER',
      text: `#${STORY_CF_IDS.max} Max (${clamp(b)}) is below #${STORY_CF_IDS.min} Min (${clamp(a)}): the server stores Max = Min (${stored.max}).`,
    });
  }
  if (!items.length) return null;
  const severity = items.some((it) => it.code === 'NOT_WHOLE') ? 'error' : 'warn';
  return {
    title: severity === 'error'
      ? `#${STORY_CF_IDS.min} / #${STORY_CF_IDS.max} need whole numbers from ${STORY_CF_LO} to ${STORY_CF_HI}`
      : `#${STORY_CF_IDS.min} / #${STORY_CF_IDS.max} will be adjusted on save: ${stored.min} / ${stored.max}`,
    severity,
    items,
    stored,
    logLine: `${STORY_CF_TAG} V3 hint ${STORY_CF_IDS.min}/${STORY_CF_IDS.max}=${a}-${b}: ${items.map((it) => it.code).join(',')}`
      + ` (the server stores ${stored.min}-${stored.max})`,
  };
}
